import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react"; 
import { Helmet } from 'react-helmet-async';

const API_BASE_URL = 'https://ayushvatika.com';
const IMAGE_BASE_URL = 'https://ayushvatika.com/images/products/';

// Animation styles
const customStyles = `
  .no-scrollbar::-webkit-scrollbar { display: none; }
  .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
  
  @keyframes marquee {
    0% { transform: translateX(100%); }
    100% { transform: translateX(-100%); }
  }
  .animate-slow-marquee {
    display: inline-block;
    animation: marquee 12s linear infinite;
    will-change: transform;
  }
`;

const CategoryPage = () => {
  const navigate = useNavigate();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/products`);
        const data = await response.json();
        const formatted = data.map((p: any) => {
          // Fix for multiple images from admin
          let imgPath = "default.png";
          if (p.imageUrl) {
            const list = p.imageUrl.split(',');
            imgPath = list[0].trim().replace(/[\[\]"]/g, '');
          }
          
          // Discount backend se, nahi aane par default 50
          const discountPercent = p.discount || 50; 
          
          // Original Price logic
          const originalPrice = p.mrp || p.originalPrice || Math.round(p.price / (1 - discountPercent / 100));

          // Special Offer backend se (Yahan p.offer ki jagah aap apna actual API key daal sakte ho)
          const specialOffer = p.offer ? p.offer.toString() : ""; 

          return {
            id: p.id,
            name: p.productName,
            image: `${IMAGE_BASE_URL}${imgPath}`,
            price: p.price,
            discount: discountPercent,
            originalPrice: originalPrice,
            specialOffer: specialOffer
          };
        });
        setProducts(formatted);
      } catch (e) { console.error(e); } 
      finally { setIsLoading(false); }
    };
    fetchProducts();
  }, []);

  const scroll = (offset: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: offset, behavior: "smooth" });
    }
  };

  if (isLoading) return <div className="p-20 text-center font-bold">Loading...</div>;

  return (
    <div className="bg-white py-10 px-4 md:px-12 mt-8">
      <style>{customStyles}</style>
      <Helmet><title>Trending Ayurvedic Medicines | Ayush Vatika</title></Helmet>

      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 border-b-4 border-green-700 pb-1">
          Trending <span className="text-green-700">AyushVatika</span> Products
        </h2>
        <button onClick={() => navigate("/all-products")} className="text-sm font-bold text-gray-500 hover:text-green-700 uppercase">
          View All
        </button>
      </div>

      {/* Slider Container */}
      <div className="relative group">
        <button onClick={() => scroll(-330)} className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg p-2 rounded-full border md:flex hidden">
          <ArrowLeft size={20} />
        </button>
        <button onClick={() => scroll(330)} className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg p-2 rounded-full border md:flex hidden">
          <ArrowRight size={20} />
        </button>

        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto no-scrollbar py-4 px-2 snap-x"
          style={{ scrollBehavior: 'smooth' }}
        >
          {products.map((product) => (
            <div
              key={product.id}
              className="flex-none w-[260px] md:w-[290px] bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 snap-start flex flex-col overflow-hidden group cursor-pointer"
              onClick={() => navigate(`/product/${product.id}`)}
            >
              {/* Product Image Section */}
              <div className="relative h-[250px] w-full bg-gray-50 overflow-hidden">
                <div className="absolute top-3 left-0 bg-red-600 text-white font-bold text-[10px] px-3 py-1 rounded-r-full shadow-md z-10">
                  {product.discount}% OFF
                </div>
                
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Product Details Section */}
              <div className="p-4 flex flex-col flex-grow text-center bg-white border-t border-gray-50">
                <h3 className="text-sm font-bold text-gray-800 line-clamp-2 min-h-[40px] mb-2">
                  {product.name}
                </h3>

                <div className="flex items-center justify-center gap-3 mb-3">
                   <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                   <span className="text-xl font-black text-green-700">₹{product.price}</span>
                </div>

                {/* Marquee 1: Discount (Always Shows) */}
                <div className="w-full overflow-hidden h-6 mb-1 bg-red-50 flex items-center rounded-sm">
                  <p className="text-[10px] text-red-600 font-bold whitespace-nowrap animate-slow-marquee">
                    ⚡ Special {product.discount}% Discount Applied! Limited Time Offer ⚡
                  </p>
                </div>

                {/* Marquee 2: Special Offer (Shows ONLY if backend sends valid text) */}
                {product.specialOffer && product.specialOffer.trim() !== "" && (
                  <div className="w-full overflow-hidden h-6 mb-4 bg-green-50 flex items-center rounded-sm">
                    <p className="text-[10px] text-green-700 font-bold whitespace-nowrap animate-slow-marquee">
                      🎁 SPECIAL DEAL: {product.specialOffer} 🎁
                    </p>
                  </div>
                )}
                
                {/* Agar offer nahi hai toh button niche touch na ho isliye ek chota space */}
                {(!product.specialOffer || product.specialOffer.trim() === "") && <div className="mb-4"></div>}

                <button className="w-full bg-green-800 text-white py-2.5 rounded-lg font-bold text-xs uppercase hover:bg-green-900 transition-colors shadow-sm mt-auto">
                  Add to Bag
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;